import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { connect } from 'react-redux'
import Field from 'components/forms/Field'
import routes from 'config/routes'
import { CurrencyContext } from 'contexts'
import { Form, Formik } from 'formik'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import React from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { parseError } from 'utils'
import * as Yup from 'yup'
import LoadingIcon from './LoadingIcon'

function ExpenseForm(props) {
  const currency = React.useContext(CurrencyContext)
  const initial = {
    description: '',
    date: moment().format('yyyy-MM-DD'),
    amount: 0.0,
    player: props.username,
    ...props.initial,
  }

  // add custom values to initial
  props.customFields.forEach(
    custom =>
      (initial[`custom_${custom.id}`] = props.initial?.custom
        ? props.initial?.custom[custom.id] || ''
        : ''),
  )

  const validate = values => {
    const errors = {}
    // Truncate amount to two decimal places
    if (values.amount) {
      values.amount = parseFloat(values.amount).toFixed(2)
    }
    return errors
  }

  const ExpenseSchema = Yup.object().shape({
    description: Yup.string().required('Required'),
    player: Yup.string(),
    date: Yup.string().required('Required'),
    amount: Yup.number().required('Required'),
  })

  const submit = async (values, actions) => {
    const { ...rest } = values
    const custom = {}
    Object.keys(rest).forEach(key => {
      if (key.startsWith('custom_')) {
        custom[key.slice(7)] = rest[key]
        delete rest[key]
      }
    })
    const prepared = {
      ...rest,
      custom,
    }
    try {
      await props.onSubmit(prepared)
    } catch (err) {
      const { field_errors } = parseError(err)
      actions.setErrors(field_errors)
      console.error('Error submitting expense')
      console.error(err)
    }
  }

  return (
    <Card className="shadow">
      <Card.Body>
        <Card.Title>{props.editing ? 'EDIT' : 'ADD'} EXPENSE</Card.Title>
        <Formik
          enableReinitialize
          initialValues={initial}
          onSubmit={submit}
          validate={validate}
          validationSchema={ExpenseSchema}
        >
          {({ isSubmitting }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <Field name="description" />
                  {props.showPlayerField && <Field name="player" />}
                  <Field
                    name="amount"
                    type="number"
                    step="any"
                    prepend={
                      <FontAwesomeIcon icon={currency.icon} fixedWidth />
                    }
                  />
                  <Field name="date" type="date" />
                </Col>
              </Row>
              <Row className="my-3">
                <Col lg={6}>
                  <div className="custom-fields-title h6">Custom Fields</div>
                  {props.customFields.length > 0 ? (
                    <>
                      {props.customFields.map(custom => (
                        <Field
                          key={custom.id}
                          name={`custom_${custom.id}`}
                          label={custom.title}
                        />
                      ))}
                      <p className="small">
                        <Link to={routes.settings}>Manage custom fields</Link>
                      </p>
                    </>
                  ) : (
                    <p className="small">
                      <Link to={routes.settings}>Add custom fields</Link> to
                      track any additional information about your expenses that
                      you think is useful.
                    </p>
                  )}
                </Col>
              </Row>
              <Button variant="warning" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <LoadingIcon size="xs" /> : 'SAVE'}
              </Button>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  )
}

ExpenseForm.propTypes = {
  customFields: PropTypes.array,
  editing: PropTypes.bool,
  initial: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  showPlayerField: PropTypes.bool,
}

ExpenseForm.defaultProps = {
  customFields: [],
  editing: false,
  initial: {},
  showPlayerField: false,
}

const mapState = state => ({
  username: state.auth.username,
})

export default connect(mapState)(ExpenseForm)
